import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './SIPCalculator.css';

function formatINR(amount) {
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} L`;
  return `₹${Math.round(amount).toLocaleString('en-IN')}`;
}

function calculateSIP(monthly, rate, years) {
  const n = years * 12;
  const r = rate / 100 / 12;
  const invested = monthly * n;
  const value = r === 0
    ? invested
    : monthly * ((Math.pow(1 + r, n) - 1) / r) * (1 + r);
  const returns = value - invested;
  return { invested, value, returns };
}

export default function SIPCalculator() {
  const [monthly, setMonthly] = useState(1000);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(10);

  const { invested, value, returns } = calculateSIP(monthly, rate, years);
  const gainPct = Math.round((returns / invested) * 100);

  const milestones = [5, 10, 15, 20, 25, 30].filter(y => y <= years + 1);
  const maxValue = calculateSIP(monthly, rate, Math.max(...milestones)).value;

  return (
    <div className="sip-page">
      <Helmet>
        <title>SIP Calculator — DhanAcademy</title>
        <meta name="description" content="See exactly how much your monthly SIP investment grows over time. Free, no signup." />
      </Helmet>

      <div className="sip-topbar">
        <div className="sip-topbar-inner">
          <Link to="/" className="back-link">← Home</Link>
          <span className="tool-badge">Tool</span>
        </div>
      </div>

      <div className="sip-inner">

        <div className="sip-header">
          <h1 className="sip-title">SIP Calculator</h1>
          <p className="sip-sub">See exactly how much your monthly investment grows over time</p>
        </div>

        <div className="sip-layout">

          <div className="sip-inputs">

            <div className="input-card">
              <div className="input-group">
                <div className="input-label-row">
                  <span className="input-label">Monthly investment</span>
                  <span className="input-value">₹{monthly.toLocaleString('en-IN')}</span>
                </div>
                <input
                  type="range"
                  min="500"
                  max="1000000"
                  step="500"
                  value={monthly}
                  onChange={e => setMonthly(Number(e.target.value))}
                  className="sip-slider"
                />
                <div className="slider-ends">
                  <span>₹500</span>
                  <span>₹10,00,000</span>
                </div>
              </div>

              <div className="input-group">
                <div className="input-label-row">
                  <span className="input-label">Expected annual return</span>
                  <span className="input-value">{rate}%</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="30"
                  step="1"
                  value={rate}
                  onChange={e => setRate(Number(e.target.value))}
                  className="sip-slider"
                />
                <div className="slider-ends">
                  <span>1%</span>
                  <span>30%</span>
                </div>
                <div className="rate-hints">
                  <span className={rate <= 7 ? 'hint-active' : 'hint'} onClick={() => setRate(7)}>FD ~7%</span>
                  <span className={rate === 12 ? 'hint-active' : 'hint'} onClick={() => setRate(12)}>Nifty 50 ~12%</span>
                  <span className={rate === 15 ? 'hint-active' : 'hint'} onClick={() => setRate(15)}>Midcap ~15%</span>
                </div>
              </div>

              <div className="input-group">
                <div className="input-label-row">
                  <span className="input-label">Investment duration</span>
                  <span className="input-value">{years} years</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="40"
                  step="1"
                  value={years}
                  onChange={e => setYears(Number(e.target.value))}
                  className="sip-slider"
                />
                <div className="slider-ends">
                  <span>1 yr</span>
                  <span>40 yrs</span>
                </div>
              </div>
            </div>

            <div className="result-summary">
              <div className="result-row">
                <div className="result-item">
                  <div className="result-label">Total invested</div>
                  <div className="result-amount invested">{formatINR(invested)}</div>
                </div>
                <div className="result-divider"></div>
                <div className="result-item">
                  <div className="result-label">Returns earned</div>
                  <div className="result-amount returns">{formatINR(returns)}</div>
                </div>
                <div className="result-divider"></div>
                <div className="result-item">
                  <div className="result-label">Total value</div>
                  <div className="result-amount total">{formatINR(value)}</div>
                </div>
              </div>
              <div className="gain-bar-wrap">
                <div className="gain-bar-track">
                  <div
                    className="gain-bar-invested"
                    style={{ width: `${Math.round((invested / value) * 100)}%` }}
                  ></div>
                </div>
                <div className="gain-bar-labels">
                  <span className="gbl invested-lbl">Invested {Math.round((invested / value) * 100)}%</span>
                  <span className="gbl returns-lbl">Returns {Math.round((returns / value) * 100)}%</span>
                </div>
              </div>
              <div className="gain-pill">
                Your money grows by <strong>{gainPct}%</strong> — returns are {formatINR(returns)} on top of what you put in
              </div>
            </div>
          </div>

          <div className="sip-chart-col">
            <div className="chart-card">
              <div className="chart-title">Growth at each milestone</div>
              <div className="chart-bars">
                {milestones.map(y => {
                  const { invested: inv, value: val } = calculateSIP(monthly, rate, y);
                  const barH = Math.round((val / maxValue) * 200);
                  const invH = Math.round((inv / maxValue) * 200);
                  return (
                    <div key={y} className="chart-col-wrap">
                      <div className="chart-val-label">{formatINR(val)}</div>
                      <div className="chart-bar-wrap" style={{ height: 200 }}>
                        <div className="chart-bar" style={{ height: barH }}>
                          <div className="chart-bar-ret" style={{ height: barH - invH }}></div>
                          <div className="chart-bar-inv" style={{ height: invH }}></div>
                        </div>
                      </div>
                      <div className="chart-year-label">{y}yr</div>
                    </div>
                  );
                })}
              </div>
              <div className="chart-legend">
                <span><span className="cl-dot inv-dot"></span>Invested</span>
                <span><span className="cl-dot ret-dot"></span>Returns</span>
              </div>
            </div>

            <div className="insight-card">
              <div className="insight-label">KEY INSIGHT</div>
              <p className="insight-text">
                Investing <strong>{formatINR(monthly)}/month</strong> for <strong>{years} years</strong> at <strong>{rate}%</strong> turns <strong>{formatINR(invested)}</strong> into <strong>{formatINR(value)}</strong>. Your money multiplies <strong>{(value / invested).toFixed(1)}x</strong>.
              </p>
              {rate >= 12 && (
                <p className="insight-note">
                  A Nifty 50 index fund has historically returned ~12% annually. Past returns do not guarantee future performance.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
